import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ExternalLink, UserPlus, Target } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ContactFormDialog from "@/components/crm/ContactFormDialog";
import { tr } from "@/lib/i18n";
import { UK_AGENT_TEMPLATE } from "@/constants/ukAgentTemplate";

/**
 * Static Career Path template: "Znaleźć agenta w UK / Find an Agent in the UK".
 *
 * This route is protected by the tile on /career (FREE users see the tile
 * but hit the existing WhatNextPaywall kind='career'; LEO START / MONTHLY
 * users navigate here). The content itself is 100% static — no LLM, no
 * external fetches, no goal record created — it just exposes an "Add to CRM"
 * shortcut that reuses the existing ContactFormDialog with a prefilled
 * `type: "agent_uk"`.
 */
export default function UkAgentTemplate() {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const nav = useNavigate();
  const [prefill, setPrefill] = useState(null);
  const [openSections, setOpenSections] = useState(() => new Set(["A"])); // A open by default

  const L = UK_AGENT_TEMPLATE[lang === "en" ? "en" : "pl"];

  const toggleSection = (key) => {
    setOpenSections((prev) => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return s;
    });
  };

  const openAddToCrm = (agency) => {
    setPrefill({
      first_name: agency.name,
      last_name: "",
      type: "agent_uk",
      company: "",
      email: agency.email || "",
      project_context: agency.website || "",
      notes: agency.website ? (lang === "pl" ? `Strona: ${agency.website}` : `Website: ${agency.website}`) : "",
    });
  };

  return (
    <div className="relative" data-testid="uk-agent-template-page">
      <button
        onClick={() => nav("/career")}
        className="mono text-[10px] text-stone-500 hover:text-amber-500 uppercase tracking-widest flex items-center gap-1 mb-4"
        data-testid="uk-agent-back-btn"
      >
        <ChevronLeft size={11} /> {L.back}
      </button>

      <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase flex items-center gap-2">
        <Target size={12} /> {L.eyebrow}
      </div>
      <h1 className="serif text-4xl sm:text-5xl text-stone-100 mt-2">{L.title}</h1>

      <div className="mt-10 space-y-12 max-w-4xl">
        {L.steps.map((step, idx) => (
          <motion.section
            key={idx}
            data-testid={`uk-agent-step-${idx + 1}`}
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.35 }}
          >
            <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
              {L.stepLabel} {String(idx + 1).padStart(2, "0")}
            </div>
            <h2 className="serif text-2xl sm:text-3xl text-stone-100 mt-1">{step.title}</h2>

            {step.body && (
              <div className="text-stone-300 leading-relaxed mt-4 whitespace-pre-line">
                {step.body}
              </div>
            )}

            {step.fields && (
              <ul className="mt-5 space-y-3">
                {step.fields.map((f, i) => (
                  <li key={i} className="paper p-4">
                    <div className="serif text-lg text-stone-100">{f.label}</div>
                    <div className="text-stone-400 text-sm mt-1">{f.hint}</div>
                  </li>
                ))}
              </ul>
            )}

            {step.checklist && (
              <ul className="mt-5 space-y-2">
                {step.checklist.map((c, i) => (
                  <li key={i}>
                    <label className="paper p-3 flex items-center gap-3 cursor-pointer hover:border-amber-500/50">
                      <input
                        type="checkbox"
                        data-testid={`uk-agent-check-${idx + 1}-${i}`}
                        className="w-4 h-4 accent-amber-500"
                      />
                      <span className="text-stone-200 text-sm">{c}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}

            {step.effect && (
              <div className="mt-5 paper p-4 border-amber-500/30 bg-amber-500/5 text-amber-100 text-sm whitespace-pre-line">
                {step.effect}
              </div>
            )}

            {step.sections && step.sections.map((sec) => (
              <div key={sec.key} className="mt-6">
                <button
                  data-testid={`uk-agent-section-${sec.key}`}
                  onClick={() => toggleSection(sec.key)}
                  className="w-full paper p-4 text-left flex items-center justify-between hover:border-amber-500/50"
                >
                  <span className="serif text-xl text-stone-100">{sec.title}</span>
                  <span className="mono text-[10px] uppercase tracking-widest text-amber-500">
                    {openSections.has(sec.key) ? L.collapse : L.expand}
                  </span>
                </button>
                {openSections.has(sec.key) && (
                  <ul className="mt-3 space-y-2">
                    {sec.agencies.map((a, i) => (
                      <li key={i} className="paper p-4">
                        <div className="flex items-baseline justify-between gap-3 flex-wrap">
                          <div className="serif text-lg text-stone-100">{a.name}</div>
                        </div>
                        {a.website && (
                          <div className="mono text-xs text-stone-400 mt-1 break-all">{a.website}</div>
                        )}
                        {a.email ? (
                          <div className="mono text-xs text-amber-500/80 mt-1 break-all">{a.email}</div>
                        ) : (
                          <div className="mono text-[11px] text-stone-500 mt-1 italic">{L.noEmailNote}</div>
                        )}
                        {a.note && (
                          <div className="text-stone-300 text-sm mt-2">{a.note}</div>
                        )}
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {a.website && (
                            <a
                              data-testid={`uk-agent-open-${a.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                              href={`https://${a.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ghost-btn !py-2 !text-xs"
                            >
                              <ExternalLink size={12} /> {L.openWebsite}
                            </a>
                          )}
                          <button
                            data-testid={`uk-agent-add-${a.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                            onClick={() => openAddToCrm(a)}
                            className="pill-btn !py-2 !text-xs"
                          >
                            <UserPlus size={12} /> {L.addToCrm}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {step.underListNote && (
              <div className="mt-6 text-stone-300 leading-relaxed whitespace-pre-line">
                {step.underListNote}
              </div>
            )}

            {step.moreLinks && (
              <div className="mt-4">
                <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase mb-2">
                  {step.moreLinksHeading}
                </div>
                <ul className="space-y-2">
                  {step.moreLinks.map((m, i) => (
                    <li key={i} className="paper p-3">
                      <div className="text-stone-100 text-sm">{m.name}</div>
                      <a
                        href={`https://${m.website.replace(/^https?:\/\//, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mono text-xs text-amber-500 hover:text-amber-400 break-all"
                      >
                        {m.website}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.section>
        ))}
      </div>

      <ContactFormDialog
        open={prefill !== null}
        onOpenChange={(v) => { if (!v) setPrefill(null); }}
        prefill={prefill}
        onSaved={() => setPrefill(null)}
      />
    </div>
  );
}
